/**
 * Family Vault — permanent digital archive for a family's educational journey.
 * Apple Wallet × Google Drive × bank safe deposit box.
 */

import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { toast } from "sonner";
import { DigitalWalletCards } from "@/components/digital-wallet-cards";
import {
  FileText, Award, Receipt, Heart, Bus, BookOpen, Shield,
  Download, Printer, X, ChevronRight, CheckCircle, CheckCircle2,
  GraduationCap, User, Calendar, MapPin, Search, Eye, Share2,
  Link2, Trash2, Upload, Sparkles, Tag, AlertCircle, AlertTriangle,
  History, Clock, Folder, Copy, ExternalLink, BadgeCheck,
  Lock, FileCheck, FileSignature, Stamp, ShieldCheck, KeyRound,
  IdCard, BookMarked, Trophy, Stethoscope, CreditCard,
  TrendingUp, Zap,
} from "lucide-react";

// ─── Design tokens (Apple system) ──────────────────────────────────────────
const C = {
  text: "#1C1917", textMid: "#3C3530", textMuted: "#8B6F47", textLight: "#A68A5A",
  bg: "#EDE4D4", card: "#FFFFFF", border: "#E2D6C0", cardBorder: "#E2D6C0",
  accent: "#7B1416", accentLight: "#F5EFE4",
  green: "#C4A882", greenLight: "#E3F9E5",
  red: "#B33A1A", redLight: "#FEE5E5",
  orange: "#C4A882", orangeLight: "#FFF4E5",
  teal: "#C4A882", tealLight: "#E6FFFE",
  purple: "#8B6F47", purpleLight: "#F5E6FD",
  pink: "#FF2D55", pinkLight: "#FFE8ED",
  blue: "#7B1416", blueLight: "#F5EFE4",
  gold: "#FFCC00", goldLight: "#FFFADC",
  amber: "#C4A882", amberLight: "#FFF4E5",
} as const;

const EASE = [0.4, 0, 0.2, 1] as const;

// ─── Collections (intelligent, not folders) ─────────────────────────────────
type Collection = {
  id: string; label: string; icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  color: string; bg: string; count: number;
};

const COLLECTIONS: Collection[] = [
  { id: "academic", label: "Report Cards", icon: Award, color: C.blue, bg: C.blueLight, count: 4 },
  { id: "financial", label: "Receipts", icon: Receipt, color: C.gold, bg: C.goldLight, count: 6 },
  { id: "medical", label: "Medical", icon: Stethoscope, color: C.pink, bg: C.pinkLight, count: 3 },
  { id: "transport", label: "Transport", icon: Bus, color: C.teal, bg: C.tealLight, count: 2 },
  { id: "learning", label: "Learning", icon: BookOpen, color: C.purple, bg: C.purpleLight, count: 5 },
  { id: "identity", label: "Identity", icon: IdCard, color: C.accent, bg: C.accentLight, count: 4 },
  { id: "certificates", label: "Certificates", icon: GraduationCap, color: C.green, bg: C.greenLight, count: 3 },
  { id: "consent", label: "Consent Forms", icon: Shield, color: C.orange, bg: C.orangeLight, count: 2 },
];

// ─── Vault document type ────────────────────────────────────────────────────
interface VaultDoc {
  id: string; title: string; category: string; type: string;
  student_name: string; school_name: string; class: string;
  issued_at: string; expires_at: string | null;
  file_size: string; file_type: string;
  status: "valid" | "pending_signature" | "expired";
  priority: "normal" | "high" | "urgent";
  verified: boolean; downloaded: boolean; shared: boolean;
  has_updates: boolean; versions: number;
  description: string; tags: string[];
  amount?: string; school_badge?: boolean;
  file_url?: string | null; file_name?: string | null;
}

const VAULT_DOCS: VaultDoc[] = [
  { id: "d1", title: "Primary 6 Report Card — Term 2", category: "academic", type: "report-card", student_name: "Emma Nakamya", school_name: "Greenhill Academy", class: "Primary 6A", issued_at: "2025-06-28", expires_at: null, file_size: "245 KB", file_type: "PDF", status: "valid", priority: "normal", verified: true, downloaded: false, shared: false, has_updates: true, versions: 2, description: "Official academic report showing term performance across all subjects with teacher remarks.", tags: ["Report", "Academic", "Term 2"] },
  { id: "d2", title: "Term 3 Fee Invoice", category: "financial", type: "fee-invoice", student_name: "Emma Nakamya", school_name: "Greenhill Academy", class: "Primary 6A", issued_at: "2025-07-01", expires_at: "2025-07-15", file_size: "128 KB", file_type: "PDF", status: "valid", priority: "high", verified: true, downloaded: true, shared: false, has_updates: false, versions: 1, description: "Official fee invoice for Term 3. Payment due by July 15, 2025.", tags: ["Invoice", "Fees"], amount: "UGX 1,850,000", school_badge: true },
  { id: "d3", title: "Medical Consent Form", category: "medical", type: "medical-consent", student_name: "Emma Nakamya", school_name: "Greenhill Academy", class: "Primary 6A", issued_at: "2025-01-15", expires_at: "2025-12-31", file_size: "89 KB", file_type: "PDF", status: "valid", priority: "normal", verified: true, downloaded: true, shared: true, has_updates: false, versions: 1, description: "Parental authorization for medical treatment and emergency care.", tags: ["Medical", "Consent"] },
  { id: "d4", title: "Transport Agreement — Route B", category: "transport", type: "transport-agreement", student_name: "Emma Nakamya", school_name: "Greenhill Academy", class: "Primary 6A", issued_at: "2025-02-01", expires_at: "2025-12-15", file_size: "156 KB", file_type: "PDF", status: "valid", priority: "normal", verified: true, downloaded: true, shared: false, has_updates: false, versions: 1, description: "School transport service agreement with route and schedule details.", tags: ["Transport", "Bus", "Route B"] },
  { id: "d5", title: "Science Trip Consent Form", category: "consent", type: "trip-consent", student_name: "Emma Nakamya", school_name: "Greenhill Academy", class: "Primary 6A", issued_at: "2025-07-01", expires_at: "2025-07-07", file_size: "67 KB", file_type: "PDF", status: "pending_signature", priority: "urgent", verified: true, downloaded: false, shared: false, has_updates: true, versions: 1, description: "Field trip permission form for Uganda Museum visit. Action required.", tags: ["Consent", "Trip", "Urgent"], school_badge: true },
  { id: "d6", title: "Admission Letter — 2024", category: "academic", type: "admission", student_name: "Emma Nakamya", school_name: "Greenhill Academy", class: "Primary 6A", issued_at: "2024-01-20", expires_at: null, file_size: "198 KB", file_type: "PDF", status: "valid", priority: "normal", verified: true, downloaded: true, shared: true, has_updates: false, versions: 1, description: "Official admission letter confirming enrollment at Greenhill Academy.", tags: ["Admission", "Enrollment"] },
  { id: "d7", title: "Receipt — Term 2 Fee Payment", category: "financial", type: "receipt", student_name: "Emma Nakamya", school_name: "Greenhill Academy", class: "Primary 6A", issued_at: "2025-05-15", expires_at: null, file_size: "78 KB", file_type: "PDF", status: "valid", priority: "normal", verified: true, downloaded: true, shared: false, has_updates: false, versions: 1, description: "Proof of payment for Term 2 fees.", tags: ["Receipt", "Payment"], amount: "UGX 1,200,000" },
  { id: "d8", title: "Vaccination Record", category: "medical", type: "vaccination", student_name: "Emma Nakamya", school_name: "Greenhill Academy", class: "Primary 6A", issued_at: "2024-08-10", expires_at: null, file_size: "56 KB", file_type: "PDF", status: "valid", priority: "normal", verified: true, downloaded: false, shared: false, has_updates: false, versions: 1, description: "Immunization record showing all required vaccinations are up to date.", tags: ["Vaccination", "Health"] },
];

// ─── Document card — premium object, not a row ──────────────────────────────
function VaultCard({
  doc, index, onSelect, onDownload, onShare,
}: {
  doc: VaultDoc; index: number;
  onSelect: () => void; onDownload: () => void; onShare: () => void;
}) {
  const [isHovered, setIsHovered] = useState(false);
  const collection = COLLECTIONS.find(c => c.id === doc.category) ?? COLLECTIONS[0];
  const Icon = collection.icon;
  const isUrgent = doc.priority === "urgent" || doc.status === "pending_signature";
  const daysUntilExpiry = doc.expires_at ? Math.ceil((new Date(doc.expires_at).getTime() - Date.now()) / (86400000)) : null;
  const isExpiringSoon = daysUntilExpiry !== null && daysUntilExpiry <= 7 && daysUntilExpiry > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.05, 0.4), duration: 0.4, ease: EASE }}
      whileHover={{ y: -3 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      onClick={onSelect}
      className="group break-inside-avoid mb-4 cursor-pointer"
    >
      <div
        className="relative overflow-hidden rounded-[20px] border bg-white transition-all"
        style={{
          borderColor: isUrgent ? `${C.red}40` : `${C.cardBorder}`,
          boxShadow: isHovered
            ? "0 12px 32px -8px rgba(0,0,0,0.1), 0 0 0 0.5px rgba(0,0,0,0.04)"
            : "0 1px 3px rgba(0,0,0,0.04), 0 8px 24px -6px rgba(0,0,0,0.06)",
        }}
      >
        {/* Urgent / expiring indicator */}
        {(isUrgent || isExpiringSoon) && (
          <div className="h-0.5" style={{ background: isUrgent ? C.red : C.orange }} />
        )}

        <div className="p-5">
          {/* Header */}
          <div className="flex items-start gap-3 mb-3">
            <div className="h-11 w-11 rounded-[14px] flex items-center justify-center shrink-0" style={{ background: collection.bg }}>
              <Icon className="h-5 w-5" style={{ color: collection.color }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 mb-1">
                <span className="text-[10px] font-semibold uppercase tracking-tight px-1.5 py-0.5 rounded" style={{ background: `${collection.color}10`, color: collection.color }}>
                  {collection.label}
                </span>
                {doc.has_updates && (
                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded" style={{ background: `${C.accent}10`, color: C.accent }}>Updated</span>
                )}
              </div>
              <h4 className="text-sm font-semibold tracking-tight leading-snug line-clamp-2" style={{ color: C.text }}>{doc.title}</h4>
              <p className="text-xs truncate mt-0.5" style={{ color: C.textMuted }}>{doc.student_name} · {doc.class}</p>
            </div>
            {/* Verification seal */}
            {doc.verified && (
              <div className="shrink-0 flex flex-col items-center gap-0.5">
                <BadgeCheck className="h-4 w-4" style={{ color: C.green }} />
                <span className="text-[8px] font-semibold" style={{ color: C.green }}>Verified</span>
              </div>
            )}
          </div>

          {/* Description */}
          <p className="text-xs leading-relaxed mb-3 line-clamp-2" style={{ color: C.textMuted }}>{doc.description}</p>

          {/* Tags */}
          <div className="flex flex-wrap gap-1.5 mb-3">
            {doc.tags.slice(0, 3).map((tag, i) => (
              <span key={i} className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: C.bg, color: C.textMuted }}>{tag}</span>
            ))}
          </div>

          {/* Status + actions */}
          <div className="flex items-center justify-between pt-3 border-t border-[#E2D6C0]/60">
            <div className="flex items-center gap-2.5 text-[10px]" style={{ color: C.textMuted }}>
              <span className="flex items-center gap-0.5"><Calendar className="h-3 w-3" />{new Date(doc.issued_at).toLocaleDateString("en-UG", { month: "short", day: "numeric" })}</span>
              <span>{doc.file_size}</span>
              {doc.versions > 1 && <span className="flex items-center gap-0.5"><History className="h-3 w-3" />v{doc.versions}</span>}
            </div>
            <div className="flex items-center gap-1">
              {doc.status === "pending_signature" ? (
                <motion.button whileTap={{ scale: 0.9 }} onClick={(e) => { e.stopPropagation(); onSelect(); }}
                  className="text-[10px] font-semibold px-2.5 py-1 rounded-full text-white" style={{ background: C.red }}>
                  Action Required
                </motion.button>
              ) : (
                <>
                  <button onClick={(e) => { e.stopPropagation(); onDownload(); }} className="h-7 w-7 rounded-lg flex items-center justify-center hover:bg-[#EDE4D4] transition-colors" style={{ color: C.textMuted }}>
                    <Download className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); onShare(); }} className="h-7 w-7 rounded-lg flex items-center justify-center hover:bg-[#EDE4D4] transition-colors" style={{ color: C.textMuted }}>
                    <Share2 className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); onSelect(); }} className="h-7 w-7 rounded-lg flex items-center justify-center hover:bg-[#EDE4D4] transition-colors" style={{ color: C.textMuted }}>
                    <Eye className="h-3.5 w-3.5" />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ─── Main component ─────────────────────────────────────────────────────────
export function ParentDocuments() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [selectedCollection, setSelectedCollection] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDoc, setSelectedDoc] = useState<VaultDoc | null>(null);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [uploadCategory, setUploadCategory] = useState<string>("academic");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Real uploaded docs from DB
  const { data: dbDocs = [] } = useQuery({
    queryKey: ["parent_documents", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase.from("parent_documents").select("*").eq("owner_id", user!.id).order("created_at", { ascending: false });
      return data ?? [];
    },
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) return;
    const valid = files.filter(f => f.size <= 10 * 1024 * 1024);
    if (valid.length < files.length) toast.error("Some files exceed 10MB and were skipped");
    setUploadFiles(prev => [...prev, ...valid]);
  };

  const removeFile = (idx: number) => setUploadFiles(prev => prev.filter((_, i) => i !== idx));

  const handleUpload = async () => {
    if (!user || uploadFiles.length === 0) return;
    setUploading(true);
    try {
      for (const file of uploadFiles) {
        const ext = file.name.split('.').pop() ?? 'pdf';
        const path = `${user.id}/${Date.now()}-${Math.random().toString(36).slice(2,8)}.${ext}`;
        const { error: upErr } = await supabase.storage.from("parent-vault").upload(path, file);
        if (upErr) throw upErr;
        const sizeStr = file.size > 1024*1024 ? `${(file.size/1024/1024).toFixed(1)} MB` : `${Math.round(file.size/1024)} KB`;
        const { error: dbErr } = await supabase.from("parent_documents").insert({
          owner_id: user.id,
          title: file.name.replace(/\.[^.]+$/, ''),
          category: uploadCategory,
          file_url: path,
          file_name: file.name,
          file_size: file.size,
          file_type: file.type || ext,
          description: `Uploaded by parent · ${sizeStr}`,
          tags: [uploadCategory],
        });
        if (dbErr) throw dbErr;
      }
      toast.success(`${uploadFiles.length} file${uploadFiles.length > 1 ? 's' : ''} uploaded to vault`);
      setUploadFiles([]); setShowUpload(false);
      qc.invalidateQueries({ queryKey: ["parent_documents"] });
    } catch (e: any) {
      toast.error(e?.message ?? "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const [signing, setSigning] = useState(false);
  const [signedIds, setSignedIds] = useState<Set<string>>(new Set());
  const [signedLog, setSignedLog] = useState<VaultDoc[]>([]);

  const handleSign = async (doc: VaultDoc) => {
    if (!user) { toast.error("You must be signed in"); return; }
    setSigning(true);
    try {
      const isDbDoc = doc.id.length > 10 && doc.file_url;
      if (isDbDoc) {
        const { error } = await supabase
          .from("parent_documents")
          .update({ status: "signed", signed_at: new Date().toISOString(), signed_by: user.id })
          .eq("id", doc.id)
          .eq("owner_id", user.id);
        if (error) throw error;
      }
      setSignedIds(prev => new Set(prev).add(doc.id));
      setSignedLog(prev => [{ ...doc, status: "valid" as const }, ...prev]);
      setSelectedDoc(null);
      toast.success(`"${doc.title}" signed and archived`);
      qc.invalidateQueries({ queryKey: ["parent_documents"] });

      // Send confirmation email + SMS via edge function
      try {
        const userEmail = user.email ?? user.user_metadata?.email ?? null;
        const userPhone = user.user_metadata?.phone ?? user.user_metadata?.phone_number ?? null;
        const fnUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-signature-confirmation`;
        fetch(fnUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            doc_id: doc.id,
            doc_title: doc.title,
            student_name: doc.student_name,
            school_name: doc.school_name,
            user_id: user.id,
            user_email: userEmail,
            user_phone: userPhone,
          }),
        }).catch(() => {});
      } catch { /* non-blocking */ }
    } catch (e: any) {
      toast.error(e?.message ?? "Signing failed");
    } finally {
      setSigning(false);
    }
  };

  const handleDecline = (doc: VaultDoc) => {
    setSignedIds(prev => new Set(prev).add(doc.id));
    setSelectedDoc(null);
    toast.info(`"${doc.title}" declined`);
  };

  const filteredDocs = useMemo(() => {
    // Merge demo docs with real uploaded DB docs
    const dbVaultDocs: VaultDoc[] = dbDocs.map((d: any) => ({
      id: d.id,
      title: d.title,
      category: d.category,
      type: d.category,
      student_name: "—",
      school_name: "—",
      class: "—",
      issued_at: d.created_at,
      expires_at: d.expires_at,
      file_size: d.file_size ? (d.file_size > 1024*1024 ? `${(d.file_size/1024/1024).toFixed(1)} MB` : `${Math.round(d.file_size/1024)} KB`) : "—",
      file_type: (d.file_type ?? "").includes("pdf") ? "PDF" : (d.file_type ?? "").startsWith("image") ? "Image" : "File",
      status: d.status ?? "valid",
      priority: "normal",
      verified: false, downloaded: false, shared: false, has_updates: false, versions: 1,
      description: d.description ?? "",
      tags: d.tags ?? [],
      file_url: d.file_url,
      file_name: d.file_name,
    }));
    let docs = [...dbVaultDocs, ...VAULT_DOCS];
    docs = docs.filter(d => !signedIds.has(d.id));
    if (selectedCollection) docs = docs.filter(d => d.category === selectedCollection);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      docs = docs.filter(d =>
        d.title.toLowerCase().includes(q) ||
        d.description.toLowerCase().includes(q) ||
        d.tags.some(t => t.toLowerCase().includes(q))
      );
    }
    return docs;
  }, [selectedCollection, searchQuery, dbDocs, signedIds]);

  const urgentCount = VAULT_DOCS.filter(d => d.priority === "urgent" || d.status === "pending_signature").length;
  const verifiedCount = VAULT_DOCS.filter(d => d.verified).length;

  // Recently needed (AI prediction)
  const recentlyNeeded = [
    { label: "Birth Certificate", icon: IdCard, color: C.accent },
    { label: "Vaccination Card", icon: Stethoscope, color: C.pink },
    { label: "Fee Receipt", icon: Receipt, color: C.gold },
    { label: "Timetable", icon: Calendar, color: C.purple },
    { label: "Student ID", icon: IdCard, color: C.teal },
  ];

  // Timeline view — excludes signed/declined docs
  const timelineDocs = useMemo(() => {
    return [...VAULT_DOCS]
      .filter(d => !signedIds.has(d.id))
      .sort((a, b) => new Date(b.issued_at).getTime() - new Date(a.issued_at).getTime());
  }, [signedIds]);

  const handleDownload = (doc: VaultDoc) => toast.success(`Downloading "${doc.title}"…`);
  const handleShare = (doc: VaultDoc) => {
    navigator.clipboard.writeText(`https://trisphere.ac.ug/vault/${doc.id}`);
    toast.success("Secure link copied — expires in 24 hours");
  };

  return (
    <div className="min-h-screen" style={{ background: C.bg }}>
      <div className="px-4 sm:px-6 lg:px-8 pt-2 pb-8">
        {/* ── Editorial header — no hero, minimal ── */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }} className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Lock className="h-4 w-4" style={{ color: C.accent }} />
            <span className="text-[10px] font-semibold uppercase tracking-tight" style={{ color: C.textMuted }}>Secure Archive</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight" style={{ color: C.text }}>Family Vault</h1>
          <p className="mt-2 text-base" style={{ color: C.textMid }}>
            Every important document. <span className="font-semibold" style={{ color: C.text }}>Verified.</span> Secure. Always available.
          </p>
        </motion.div>

        {/* ── AI Search — natural language ── */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.4 }} className="mb-6">
          <div className="flex items-center gap-3 rounded-[16px] px-4 py-3.5 border" style={{ background: "rgba(255,255,255,0.72)", backdropFilter: "blur(40px) saturate(180%)", borderColor: `${C.border}60` }}>
            <Search className="h-5 w-5 shrink-0" style={{ color: C.textMuted }} />
            <input
              type="text"
              placeholder="Find Emma's report card, receipt from last term, vaccination form…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-[#8B6F47]"
              style={{ color: C.text }}
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery("")} className="p-1 rounded-full hover:bg-[#EDE4D4]">
                <X className="h-4 w-4" style={{ color: C.textMuted }} />
              </button>
            )}
          </div>
        </motion.div>

        {/* ── Quick Access Collections ── */}
        {!searchQuery && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.4 }} className="mb-8">
            <h3 className="text-base font-semibold tracking-tight mb-4" style={{ color: C.text }}>Quick Access</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
              {COLLECTIONS.map((col, i) => {
                const isActive = selectedCollection === col.id;
                return (
                  <motion.button
                    key={col.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.2 + i * 0.04 }}
                    whileHover={{ y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setSelectedCollection(isActive ? null : col.id)}
                    className="flex flex-col items-center gap-2 p-4 rounded-[18px] border transition-all"
                    style={{
                      background: isActive ? col.color : `${col.color}08`,
                      borderColor: isActive ? col.color : `${C.cardBorder}`,
                    }}
                  >
                    <div className="h-10 w-10 rounded-[12px] flex items-center justify-center" style={{ background: isActive ? "rgba(255,255,255,0.2)" : col.bg }}>
                      <col.icon className="h-5 w-5" style={{ color: isActive ? "white" : col.color }} />
                    </div>
                    <p className="text-[11px] font-semibold text-center" style={{ color: isActive ? "white" : C.text }}>{col.label}</p>
                    <p className="text-[10px]" style={{ color: isActive ? "rgba(255,255,255,0.7)" : C.textMuted }}>{col.count} docs</p>
                  </motion.button>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* ── Recently Needed (AI prediction) ── */}
        {!searchQuery && !selectedCollection && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.4 }} className="mb-8">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="h-4 w-4" style={{ color: C.purple }} />
              <h3 className="text-base font-semibold tracking-tight" style={{ color: C.text }}>Recently Needed</h3>
              <span className="text-xs" style={{ color: C.textMuted }}>· AI predicts what you may need</span>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2 no-scrollbar">
              {recentlyNeeded.map((item, i) => (
                <motion.button
                  key={i}
                  whileHover={{ y: -2 }}
                  className="shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-full border bg-white"
                  style={{ borderColor: C.cardBorder }}
                >
                  <item.icon className="h-4 w-4" style={{ color: item.color }} />
                  <span className="text-xs font-medium" style={{ color: C.text }}>{item.label}</span>
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}

        {/* ── Integrated stats ── */}
        {!searchQuery && !selectedCollection && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25 }} className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 py-3">
              <p className="text-sm" style={{ color: C.textMid }}>
                <span className="text-xl font-semibold tracking-tight" style={{ color: C.text }}>{VAULT_DOCS.length}</span> verified records in your archive
              </p>
              <span className="hidden sm:inline text-[#E2D6C0]">·</span>
              <p className="text-sm" style={{ color: C.textMid }}>
                <span className="text-xl font-semibold tracking-tight" style={{ color: C.text }}>{verifiedCount}</span> digitally signed by your school
              </p>
              {urgentCount > 0 && (
                <>
                  <span className="hidden sm:inline text-[#E2D6C0]">·</span>
                  <p className="text-sm" style={{ color: C.red }}>
                    <span className="text-xl font-semibold tracking-tight">{urgentCount}</span> need your attention
                  </p>
                </>
              )}
            </div>
          </motion.div>
        )}

        {/* ── Document grid (Pinterest masonry) ── */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold tracking-tight" style={{ color: C.text }}>
              {searchQuery ? `Results for "${searchQuery}"` : selectedCollection ? COLLECTIONS.find(c => c.id === selectedCollection)?.label : "All Documents"}
            </h2>
            <div className="flex items-center gap-2">
              <span className="text-xs" style={{ color: C.textMuted }}>{filteredDocs.length} documents</span>
              <button onClick={() => setShowUpload(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-white" style={{ background: C.accent }}>
                <Upload className="h-3.5 w-3.5" /> Upload
              </button>
            </div>
          </div>

          {filteredDocs.length === 0 ? (
            <div className="text-center py-20">
              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring" }} className="w-20 h-20 rounded-[24px] mx-auto mb-5 flex items-center justify-center" style={{ background: C.accentLight }}>
                <Folder className="h-10 w-10" style={{ color: C.accent }} />
              </motion.div>
              <h3 className="text-lg font-semibold mb-2 tracking-tight" style={{ color: C.text }}>No documents found</h3>
              <p className="text-sm" style={{ color: C.textMuted }}>Try adjusting your search or collection</p>
            </div>
          ) : (
            <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4">
              {filteredDocs.map((doc, i) => (
                <VaultCard
                  key={doc.id}
                  doc={doc}
                  index={i}
                  onSelect={() => setSelectedDoc(doc)}
                  onDownload={() => handleDownload(doc)}
                  onShare={() => handleShare(doc)}
                />
              ))}
            </div>
          )}
        </div>

        {/* ── Signed Log ── */}
        {!searchQuery && !selectedCollection && signedLog.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.28, duration: 0.5 }} className="mb-8">
            <div className="rounded-[24px] border bg-white shadow-premium overflow-hidden" style={{ borderColor: C.cardBorder }}>
              <div className="px-5 sm:px-6 py-4 border-b border-[#E2D6C0]/60 flex items-center gap-3">
                <div className="h-10 w-10 rounded-[12px] flex items-center justify-center" style={{ background: C.greenLight }}>
                  <FileSignature className="h-5 w-5" style={{ color: C.green }} />
                </div>
                <div className="flex-1">
                  <h3 className="text-base font-semibold tracking-tight" style={{ color: C.text }}>Signed Documents</h3>
                  <p className="text-xs" style={{ color: C.textMuted }}>Documents you have approved and archived</p>
                </div>
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: C.greenLight, color: C.green }}>{signedLog.length} signed</span>
              </div>
              <div className="p-5 sm:p-6 space-y-2.5">
                {signedLog.map((doc, i) => {
                  const col = COLLECTIONS.find(c => c.id === doc.category) ?? COLLECTIONS[0];
                  return (
                    <motion.div
                      key={doc.id}
                      initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.06 }}
                      className="flex items-center gap-3 p-3 rounded-xl" style={{ background: C.bg }}
                    >
                      <div className="h-9 w-9 rounded-[10px] flex items-center justify-center shrink-0" style={{ background: col.bg }}>
                        <col.icon className="h-4 w-4" style={{ color: col.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate" style={{ color: C.text }}>{doc.title}</p>
                        <p className="text-[11px]" style={{ color: C.textMuted }}>{doc.student_name} · Signed just now</p>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <BadgeCheck className="h-4 w-4" style={{ color: C.green }} />
                        <span className="text-[10px] font-semibold" style={{ color: C.green }}>Signed</span>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}

        {/* ── Timeline view ── */}
        {!searchQuery && !selectedCollection && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.5 }} className="mb-8">
            <div className="rounded-[24px] border bg-white shadow-premium overflow-hidden" style={{ borderColor: C.cardBorder }}>
              <div className="px-5 sm:px-6 py-4 border-b border-[#E2D6C0]/60 flex items-center gap-3">
                <div className="h-10 w-10 rounded-[12px] flex items-center justify-center" style={{ background: C.purpleLight }}>
                  <Clock className="h-5 w-5" style={{ color: C.purple }} />
                </div>
                <div>
                  <h3 className="text-base font-semibold tracking-tight" style={{ color: C.text }}>Document Timeline</h3>
                  <p className="text-xs" style={{ color: C.textMuted }}>Your archive in chronological order</p>
                </div>
              </div>
              <div className="p-5 sm:p-6">
                <div className="relative pl-6">
                  <div className="absolute left-2 top-2 bottom-2 w-px" style={{ background: C.border }} />
                  {timelineDocs.map((doc, i) => {
                    const col = COLLECTIONS.find(c => c.id === doc.category) ?? COLLECTIONS[0];
                    return (
                      <motion.button
                        key={doc.id}
                        initial={{ opacity: 0, x: -8 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.35 + i * 0.06 }}
                        onClick={() => setSelectedDoc(doc)}
                        className="relative flex items-center gap-4 pb-4 last:pb-0 w-full text-left"
                      >
                        <div className="absolute -left-[18px] w-4 h-4 rounded-full flex items-center justify-center" style={{ background: col.color, boxShadow: "0 0 0 3px white" }}>
                          <col.icon className="w-2.5 h-2.5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold" style={{ color: col.color }}>{new Date(doc.issued_at).toLocaleDateString("en-UG", { month: "long", day: "numeric", year: "numeric" })}</p>
                          <p className="text-sm font-medium truncate" style={{ color: C.text }}>{doc.title}</p>
                          {doc.status === "pending_signature" && (
                            <p className="text-[10px] font-semibold mt-0.5" style={{ color: C.red }}>Action Required</p>
                          )}
                        </div>
                        {doc.verified && <BadgeCheck className="h-4 w-4 shrink-0" style={{ color: C.green }} />}
                      </motion.button>
                    );
                  })}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* ── Digital Wallet — Premium Cards ── */}
        {!searchQuery && !selectedCollection && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35, duration: 0.5 }}>
            <div className="flex items-center gap-2 mb-4">
              <CreditCard className="h-4 w-4" style={{ color: C.accent }} />
              <h3 className="text-base font-semibold tracking-tight" style={{ color: C.text }}>Digital Wallet</h3>
              <span className="text-[10px] font-medium px-2 py-0.5 rounded-full" style={{ background: C.accentLight, color: C.accent }}>4 cards</span>
            </div>
            <DigitalWalletCards
              studentName="Emma Nakamya"
              studentNo="STU-2024-0042"
              photo="https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg?auto=compress&cs=tinysrgb&w=200"
              school="Greenhill Academy"
              grade="Primary 6"
              class="P6A"
            />
          </motion.div>
        )}
      </div>

      {/* ── Detail Modal ── */}
      <AnimatePresence>
        {selectedDoc && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: "rgba(0,0,0,0.4)", backdropFilter: "blur(16px)" }}
            onClick={() => setSelectedDoc(null)}
          >
            <motion.div
              initial={{ scale: 0.92, y: 24, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.92, y: 24, opacity: 0 }}
              transition={{ type: "spring", stiffness: 400, damping: 32 }}
              className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-[24px] bg-white shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header with verification seal */}
              <div className="relative px-6 py-5 overflow-hidden" style={{ background: `linear-gradient(135deg, ${COLLECTIONS.find(c => c.id === selectedDoc.category)?.color ?? C.accent} 0%, ${COLLECTIONS.find(c => c.id === selectedDoc.category)?.color ?? C.accent}cc 100%)` }}>
                <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full opacity-20" style={{ background: "white" }} />
                <div className="relative flex items-start gap-4">
                  <div className="h-14 w-14 rounded-[16px] flex items-center justify-center bg-white/20 backdrop-blur-sm">
                    {React.createElement(COLLECTIONS.find(c => c.id === selectedDoc.category)?.icon ?? FileText, { className: "h-7 w-7 text-white" })}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-tight text-white/70 mb-1">{COLLECTIONS.find(c => c.id === selectedDoc.category)?.label ?? "Document"}</p>
                    <h3 className="text-lg font-semibold text-white">{selectedDoc.title}</h3>
                  </div>
                  <button onClick={() => setSelectedDoc(null)} className="h-9 w-9 rounded-xl flex items-center justify-center bg-white/10 hover:bg-white/20 transition">
                    <X className="h-4 w-4 text-white" />
                  </button>
                </div>
              </div>

              {/* Verification layer */}
              <div className="px-6 py-4 border-b border-[#E2D6C0]/60 flex items-center gap-4" style={{ background: C.greenLight }}>
                <ShieldCheck className="h-5 w-5" style={{ color: C.green }} />
                <div className="flex-1">
                  <p className="text-sm font-semibold" style={{ color: C.green }}>Verified by {selectedDoc.school_name}</p>
                  <p className="text-xs" style={{ color: C.textMid }}>Digitally signed · Timestamped · Tamper evident</p>
                </div>
                <Stamp className="h-5 w-5" style={{ color: C.green }} />
              </div>

              <div className="p-6 space-y-4">
                <p className="text-sm leading-relaxed" style={{ color: C.textMid }}>{selectedDoc.description}</p>

                {/* Meta grid */}
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: "Student", value: selectedDoc.student_name },
                    { label: "Class", value: selectedDoc.class },
                    { label: "Issued", value: new Date(selectedDoc.issued_at).toLocaleDateString("en-UG", { month: "long", day: "numeric", year: "numeric" }) },
                    { label: "File", value: `${selectedDoc.file_size} · ${selectedDoc.file_type}` },
                  ].map((m, i) => (
                    <div key={i} className="p-3 rounded-[14px]" style={{ background: C.bg }}>
                      <p className="text-[10px] font-semibold uppercase tracking-tight" style={{ color: C.textMuted }}>{m.label}</p>
                      <p className="text-sm font-semibold mt-0.5" style={{ color: C.text }}>{m.value}</p>
                    </div>
                  ))}
                </div>

                {/* Version history */}
                {selectedDoc.versions > 1 && (
                  <div className="p-4 rounded-[14px] border" style={{ borderColor: C.cardBorder, background: C.bg }}>
                    <div className="flex items-center gap-2 mb-3">
                      <History className="h-4 w-4" style={{ color: C.textMuted }} />
                      <p className="text-xs font-semibold" style={{ color: C.text }}>Version History</p>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-xs">
                        <CheckCircle2 className="h-3.5 w-3.5" style={{ color: C.green }} />
                        <span style={{ color: C.textMid }}>Version {selectedDoc.versions} — Published</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs">
                        <Clock className="h-3.5 w-3.5" style={{ color: C.textMuted }} />
                        <span style={{ color: C.textMuted }}>Version 1 — Teacher submitted</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Action required */}
                {selectedDoc.status === "pending_signature" && (
                  <div className="flex items-center gap-3 p-4 rounded-[14px]" style={{ background: `${C.red}08`, border: `1px solid ${C.red}30` }}>
                    <AlertCircle className="h-5 w-5 shrink-0" style={{ color: C.red }} />
                    <div>
                      <p className="text-sm font-semibold" style={{ color: C.red }}>Action Required</p>
                      <p className="text-xs" style={{ color: C.textMid }}>This document requires your signature by {selectedDoc.expires_at}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="px-6 py-4 border-t flex gap-3" style={{ borderColor: C.cardBorder }}>
                {selectedDoc.status === "pending_signature" ? (
                  <>
                    <button onClick={() => handleSign(selectedDoc)} disabled={signing}
                      className="flex-1 h-11 rounded-[14px] text-sm font-semibold text-white disabled:opacity-50"
                      style={{ background: C.green }}>
                      {signing ? (
                        <><motion.div animate={{ rotate: 360 }} transition={{ duration: 0.8, repeat: Infinity, ease: "linear" }} className="h-4 w-4 rounded-full inline-block mr-2 align-middle" style={{ border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "#fff" }} />Signing…</>
                      ) : (
                        <><CheckCircle className="h-4 w-4 inline mr-2" />Approve & Sign</>
                      )}
                    </button>
                    <button onClick={() => handleDecline(selectedDoc)} disabled={signing}
                      className="flex-1 h-11 rounded-[14px] text-sm font-semibold disabled:opacity-50" style={{ background: C.redLight, color: C.red }}>
                      Decline
                    </button>
                  </>
                ) : (
                  <>
                    <button onClick={() => handleDownload(selectedDoc)} className="flex-1 h-11 rounded-[14px] text-sm font-semibold text-white" style={{ background: C.accent }}>
                      <Download className="h-4 w-4 inline mr-2" />Download PDF
                    </button>
                    <button onClick={() => handleShare(selectedDoc)} className="h-11 w-11 rounded-[14px] flex items-center justify-center border" style={{ borderColor: C.cardBorder, color: C.textMid }}>
                      <Share2 className="h-4 w-4" />
                    </button>
                    <button onClick={() => { window.print(); toast.success("Opening print dialog…"); }} className="h-11 w-11 rounded-[14px] flex items-center justify-center border" style={{ borderColor: C.cardBorder, color: C.textMid }}>
                      <Printer className="h-4 w-4" />
                    </button>
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Upload Modal ── */}
      <AnimatePresence>
        {showUpload && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: "rgba(0,0,0,0.4)", backdropFilter: "blur(16px)" }}
            onClick={() => { setShowUpload(false); setUploadFiles([]); }}
          >
            <motion.div
              initial={{ scale: 0.92, y: 24, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.92, y: 24, opacity: 0 }}
              transition={{ type: "spring", stiffness: 400, damping: 32 }}
              className="w-full max-w-lg rounded-[24px] bg-white shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: C.cardBorder }}>
                <h3 className="text-base font-semibold tracking-tight" style={{ color: C.text }}>Upload to Vault</h3>
                <button onClick={() => { setShowUpload(false); setUploadFiles([]); }} className="p-2 rounded-xl hover:bg-[#EDE4D4]"><X className="h-4 w-4" style={{ color: C.textMuted }} /></button>
              </div>
              <div className="p-6">
                {/* Category selector */}
                <label className="text-xs font-bold uppercase tracking-wider mb-2 block" style={{ color: C.textMuted }}>Category</label>
                <select value={uploadCategory} onChange={e => setUploadCategory(e.target.value)}
                  className="w-full rounded-xl px-4 py-3 text-sm font-medium mb-4" style={{ border: `1px solid ${C.cardBorder}`, background: C.bg, color: C.text }}>
                  {COLLECTIONS.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>

                {/* Drop zone */}
                <input ref={fileInputRef} type="file" multiple accept="image/*,.pdf,.doc,.docx" className="hidden" onChange={handleFileSelect} />
                <div
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); }}
                  onDrop={(e) => { e.preventDefault(); const files = Array.from(e.dataTransfer.files); const valid = files.filter(f => f.size <= 10*1024*1024); setUploadFiles(prev => [...prev, ...valid]); }}
                  className="border-2 border-dashed rounded-[20px] p-10 text-center cursor-pointer transition-colors hover:bg-[#EDE4D4]"
                  style={{ borderColor: C.border, background: C.bg }}
                >
                  <Upload className="h-10 w-10 mx-auto mb-3" style={{ color: C.textMuted }} />
                  <p className="text-sm font-semibold" style={{ color: C.text }}>Drop files here or click to browse</p>
                  <p className="text-xs mt-1" style={{ color: C.textMuted }}>PDF, JPG, PNG · Max 10MB each</p>
                </div>

                {/* Selected files list */}
                {uploadFiles.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {uploadFiles.map((f, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 rounded-xl" style={{ background: C.bg, border: `1px solid ${C.cardBorder}` }}>
                        <div className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: C.accentLight }}>
                          <FileText className="h-4 w-4" style={{ color: C.accent }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold truncate" style={{ color: C.text }}>{f.name}</p>
                          <p className="text-[10px]" style={{ color: C.textMuted }}>{f.size > 1024*1024 ? `${(f.size/1024/1024).toFixed(1)} MB` : `${Math.round(f.size/1024)} KB`}</p>
                        </div>
                        <button onClick={() => removeFile(i)} className="p-1 rounded-lg hover:bg-[#E2D6C0]"><X className="h-3.5 w-3.5" style={{ color: C.textMuted }} /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="px-6 py-4 border-t flex justify-end gap-3" style={{ borderColor: C.cardBorder }}>
                <button onClick={() => { setShowUpload(false); setUploadFiles([]); }} className="px-4 py-2.5 rounded-[14px] text-sm font-semibold" style={{ background: C.bg, color: C.textMid }}>Cancel</button>
                <button onClick={handleUpload} disabled={uploadFiles.length === 0 || uploading}
                  className="px-5 py-2.5 rounded-[14px] text-sm font-semibold text-white disabled:opacity-40 flex items-center gap-2" style={{ background: C.accent }}>
                  {uploading ? (
                    <><motion.div animate={{ rotate: 360 }} transition={{ duration: 0.8, repeat: Infinity, ease: "linear" }} className="h-4 w-4 rounded-full" style={{ border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "#fff" }} /> Uploading…</>
                  ) : (
                    <><Upload className="h-4 w-4" /> Upload {uploadFiles.length > 0 ? `(${uploadFiles.length})` : ''}</>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default ParentDocuments;
